// The shine on the gold (the frames, the jackpot plaques, the pots, the coins): the glints sweeping across an element and
// the sparkles twinkling on its rim, while the game waits and on the round's moments. Seconds and design units: a glint's
// streak is the same on every element, whatever its size. The only file to edit to retune them.

import type { TweenEasing } from "@slot/presentation";

/**
 * A glint: the streak's peak brightness gain (a pixel's colour times 1 + peak at its core: gold turns toward white), its
 * lean (across per down: 0 upright, negative leaning right), its core's half-width and its soft edge beyond (design
 * units), and its sweep across the element.
 */
export interface GlintTuning {
  readonly peak: number;
  readonly lean: number;
  readonly width: number;
  readonly softness: number;
  readonly seconds: number;
  readonly easing: TweenEasing;
}

/** A sparkle: its size (design units), its life (it grows, turns and fades back) and its tint. */
export interface SparkleTuning {
  readonly size: number;
  readonly seconds: number;
  readonly tint: number;
}

export const SHINE_TUNING = {
  /** While the game waits for the player (the base game idle, the bonus before a respin): soft, by turns. */
  ambient: {
    /** A glint every this many seconds, the elements taking turns. */
    everySeconds: 5,
    /** How long the game has waited before the first: a round's last moment passes first. */
    settleSeconds: 1,
    glint: { peak: 1.5, lean: -0.55, width: 9, softness: 24, seconds: 1, easing: "sineInOut" },
    /** A sparkle on a gold rim every so often: a gap between these many seconds. */
    sparkleEverySeconds: [1.2, 2.4],
    sparkle: { size: 84, seconds: 0.7, tint: 0xfff2c0 },
  },
  /** The round's moments (the lines changing, the bonus's trigger, a pot charging, a jackpot): brighter, at once. */
  event: {
    glint: { peak: 2.2, lean: -0.55, width: 14, softness: 36, seconds: 0.7, easing: "quadOut" },
    /** A run of glints (the frame, then each pot): the gap between two. */
    staggerSeconds: 0.12,
    sparkle: { size: 100, seconds: 0.6, tint: 0xfff6d0 },
    /** The sparkles around an element on its moment, and the gap between two. */
    sparkleCount: 6,
    sparkleStaggerSeconds: 0.07,
  },
  /**
   * The coins: a wide sheen across a coin's face, much wider than the gold's streak (the user, 2026-10-07) — quick as it
   * lands (not after the player's slam), soft on its turn while the game waits.
   */
  coin: {
    land: { peak: 1.8, lean: -0.55, width: 36, softness: 60, seconds: 0.45, easing: "quadOut" },
    ambient: { peak: 1.3, lean: -0.55, width: 32, softness: 56, seconds: 1, easing: "sineInOut" },
  },
} as const satisfies {
  ambient: { everySeconds: number; settleSeconds: number; glint: GlintTuning; sparkleEverySeconds: readonly [number, number]; sparkle: SparkleTuning };
  event: { glint: GlintTuning; staggerSeconds: number; sparkle: SparkleTuning; sparkleCount: number; sparkleStaggerSeconds: number };
  coin: { land: GlintTuning; ambient: GlintTuning };
};
